// src/components/admin/CurveChart.js
import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { generateDensePoints, validateControlPoints, getMarginAtQuantity } from './curveUtils';

const CurveChart = ({ 
  controlPoints, 
  onControlPointsChange, 
  isLogScale = false,
  jobType = 'letterheads'
}) => {
  const svgRef = useRef();
  const [dimensions, setDimensions] = useState({ width: 800, height: 400 });
  const [draggedPoint, setDraggedPoint] = useState(null);
  const [isAddMode, setIsAddMode] = useState(false);
  const [hoverPoint, setHoverPoint] = useState(null);

  // Chart margins
  const margin = { top: 20, right: 50, bottom: 60, left: 80 };
  const chartWidth = dimensions.width - margin.left - margin.right;
  const chartHeight = dimensions.height - margin.top - margin.bottom;

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const container = svgRef.current?.parentElement;
      if (container) {
        setDimensions({
          width: container.clientWidth,
          height: 400
        });
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle adding new control points
  const handleAddControlPoint = (event, xScale, yScale) => {
    if (!isAddMode) return;

    const [mouseX, mouseY] = d3.pointer(event);
    const newQuantity = Math.max(1, Math.round(xScale.invert(mouseX)));
    const newMargin = Math.max(0, yScale.invert(mouseY));

    // Check if this quantity already exists
    const existingPoint = controlPoints.find(p => p.quantity === newQuantity);
    if (existingPoint) {
      alert(`A control point already exists at quantity ${newQuantity}. Please choose a different position.`);
      return;
    }

    // Create new control point
    const newPoint = { quantity: newQuantity, margin: newMargin };
    
    // Insert the new point in the correct position (sorted by quantity)
    const updatedPoints = [...controlPoints, newPoint].sort((a, b) => a.quantity - b.quantity);
    
    onControlPointsChange(updatedPoints);
    
    // Exit add mode after adding a point
    setIsAddMode(false);
    setHoverPoint(null);
  };

  // Handle mouse move for hover preview
  const handleMouseMove = (event, xScale, yScale) => {
    if (!isAddMode) {
      setHoverPoint(null);
      return;
    }

    const [mouseX, mouseY] = d3.pointer(event);
    const quantity = Math.max(1, Math.round(xScale.invert(mouseX)));
    
    // Use interpolated margin from existing curve instead of mouse position
    const interpolatedMargin = getMarginAtQuantity(controlPoints, quantity);
    
    // Check if this quantity already exists
    const existingPoint = controlPoints.find(p => p.quantity === quantity);
    if (existingPoint) {
      setHoverPoint(null);
      return;
    }

    setHoverPoint({
      quantity,
      margin: interpolatedMargin,
      x: xScale(quantity),
      y: yScale(interpolatedMargin)
    });
  };

  // Main chart rendering effect
  useEffect(() => {
    if (!controlPoints || controlPoints.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove(); // Clear previous render

    // Create scales
    const sortedPoints = [...controlPoints].sort((a, b) => a.quantity - b.quantity);
    const minQuantity = Math.min(...sortedPoints.map(p => p.quantity));
    const maxQuantity = Math.max(...sortedPoints.map(p => p.quantity));
    const minMargin = Math.min(...sortedPoints.map(p => p.margin));
    const maxMargin = Math.max(...sortedPoints.map(p => p.margin));

    // Add some padding to the scales
    const quantityPadding = (maxQuantity - minQuantity) * 0.05;
    const marginPadding = (maxMargin - minMargin) * 0.1;

    const xScale = isLogScale 
      ? d3.scaleLog()
          .domain([Math.max(1, minQuantity - quantityPadding), maxQuantity + quantityPadding])
          .range([0, chartWidth])
      : d3.scaleLinear()
          .domain([minQuantity - quantityPadding, maxQuantity + quantityPadding])
          .range([0, chartWidth]);

    const yScale = d3.scaleLinear()
      .domain([minMargin - marginPadding, maxMargin + marginPadding])
      .range([chartHeight, 0]);

    // Create main group
    const g = svg
      .attr('width', dimensions.width)
      .attr('height', dimensions.height)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Add invisible overlay for click handling and hover
    const overlay = g.append('rect')
      .attr('class', 'chart-overlay')
      .attr('width', chartWidth)
      .attr('height', chartHeight)
      .style('fill', 'transparent')
      .style('cursor', isAddMode ? 'crosshair' : 'default')
      .on('click', (event) => handleAddControlPoint(event, xScale, yScale))
      .on('mousemove', (event) => handleMouseMove(event, xScale, yScale))
      .on('mouseleave', () => setHoverPoint(null));

    // Create axes
    const xAxis = d3.axisBottom(xScale)
      .tickFormat(d3.format(',d'));
    
    const yAxis = d3.axisLeft(yScale)
      .tickFormat(d => `${d}%`);

    g.append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0,${chartHeight})`)
      .call(xAxis);

    g.append('g')
      .attr('class', 'y-axis')
      .call(yAxis);

    // Add axis labels
    g.append('text')
      .attr('class', 'x-label')
      .attr('text-anchor', 'middle')
      .attr('x', chartWidth / 2)
      .attr('y', chartHeight + 45)
      .style('font-size', '12px')
      .style('fill', '#666')
      .text('Quantity');

    g.append('text')
      .attr('class', 'y-label')
      .attr('text-anchor', 'middle')
      .attr('transform', 'rotate(-90)')
      .attr('x', -chartHeight / 2)
      .attr('y', -50)
      .style('font-size', '12px')
      .style('fill', '#666')
      .text('Margin %');

    // Generate dense points for smooth curve
    const densePoints = generateDensePoints(sortedPoints, { sampleRate: 10 });
    
    // Create line generator
    const line = d3.line()
      .x(d => xScale(d.quantity))
      .y(d => yScale(d.margin))
      .curve(d3.curveCardinal);

    // Draw the curve
    g.append('path')
      .datum(densePoints)
      .attr('class', 'curve-line')
      .attr('d', line)
      .style('fill', 'none')
      .style('stroke', '#3b82f6')
      .style('stroke-width', 2)
      .style('opacity', 0.8);

    // Add grid lines
    g.append('g')
      .attr('class', 'grid')
      .attr('transform', `translate(0,${chartHeight})`)
      .call(d3.axisBottom(xScale)
        .tickSize(-chartHeight)
        .tickFormat('')
      )
      .style('stroke-dasharray', '3,3')
      .style('opacity', 0.3);

    g.append('g')
      .attr('class', 'grid')
      .call(d3.axisLeft(yScale)
        .tickSize(-chartWidth)
        .tickFormat('')
      )
      .style('stroke-dasharray', '3,3')
      .style('opacity', 0.3);

    // Drag behavior for existing points
    const drag = d3.drag()
      .on('start', function(event, d) {
        setDraggedPoint(d);
        d3.select(this).raise().style('stroke', '#ef4444').style('stroke-width', 3);
      })
      .on('drag', function(event, d) {
        const newQuantity = Math.max(1, Math.round(xScale.invert(event.x)));
        const newMargin = Math.max(0, yScale.invert(event.y));
        
        // Update the visual position immediately
        d3.select(this)
          .attr('cx', xScale(newQuantity))
          .attr('cy', yScale(newMargin));

        // Update tooltip
        const tooltip = d3.select('.tooltip');
        if (!tooltip.empty()) {
          tooltip
            .style('left', (event.sourceEvent.pageX + 10) + 'px')
            .style('top', (event.sourceEvent.pageY - 10) + 'px')
            .html(`Quantity: ${newQuantity.toLocaleString()}<br/>Margin: ${newMargin.toFixed(1)}%`);
        }
      })
      .on('end', function(event, d) {
        const newQuantity = Math.max(1, Math.round(xScale.invert(event.x)));
        const newMargin = Math.max(0, yScale.invert(event.y));
        
        // Check for duplicate quantities
        const wouldCreateDuplicate = controlPoints.some(point => 
          point !== d && point.quantity === newQuantity
        );
        
        if (wouldCreateDuplicate) {
          // Reset to original position
          d3.select(this)
            .attr('cx', xScale(d.quantity))
            .attr('cy', yScale(d.margin));
          alert(`Quantity ${newQuantity} already exists. Please choose a different position.`);
        } else {
          // Update the control points
          const updatedPoints = controlPoints.map(point => 
            point === d ? { ...point, quantity: newQuantity, margin: newMargin } : point
          );
          onControlPointsChange(updatedPoints);
        }
        
        setDraggedPoint(null);
        
        // Reset visual styling
        d3.select(this).style('stroke', '#1f2937').style('stroke-width', 2);
        
        // Hide tooltip
        d3.select('.tooltip').style('opacity', 0);
      });

    // Add control points
    g.selectAll('.control-point')
      .data(sortedPoints)
      .enter()
      .append('circle')
      .attr('class', 'control-point')
      .attr('cx', d => xScale(d.quantity))
      .attr('cy', d => yScale(d.margin))
      .attr('r', 6)
      .style('fill', '#3b82f6')
      .style('stroke', '#1f2937')
      .style('stroke-width', 2)
      .style('cursor', isAddMode ? 'default' : 'grab')
      .call(isAddMode ? d3.drag().on('start', () => {}) : drag) // Disable drag in add mode
      .on('mouseover', function(event, d) {
        if (isAddMode) return; // Don't show tooltips in add mode
        
        // Create tooltip
        const tooltip = d3.select('body').selectAll('.tooltip')
          .data([0]);
        
        tooltip.enter()
          .append('div')
          .attr('class', 'tooltip')
          .style('position', 'absolute')
          .style('padding', '8px')
          .style('background', 'rgba(0,0,0,0.8)')
          .style('color', 'white')
          .style('border-radius', '4px')
          .style('font-size', '12px')
          .style('pointer-events', 'none')
          .style('z-index', 1000);

        d3.select('.tooltip')
          .style('opacity', 1)
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 10) + 'px')
          .html(`Quantity: ${d.quantity.toLocaleString()}<br/>Margin: ${d.margin.toFixed(1)}%`);

        // Highlight point
        d3.select(this)
          .transition()
          .duration(100)
          .attr('r', 8)
          .style('fill', '#60a5fa');
      })
      .on('mouseout', function() {
        if (isAddMode) return;
        
        d3.select('.tooltip').style('opacity', 0);
        
        // Reset point
        d3.select(this)
          .transition()
          .duration(100)
          .attr('r', 6)
          .style('fill', '#3b82f6');
      })
      .on('contextmenu', function(event, d) {
        // Right-click to delete point
        event.preventDefault();
        if (controlPoints.length <= 2) {
          alert('Cannot delete point. At least 2 control points are required.');
          return;
        }
        
        if (window.confirm(`Delete control point at quantity ${d.quantity}?`)) {
          const updatedPoints = controlPoints.filter(point => point !== d);
          onControlPointsChange(updatedPoints);
        }
      });

    // Add hover point preview when in add mode
    if (isAddMode && hoverPoint) {
      g.append('circle')
        .attr('class', 'hover-point')
        .attr('cx', hoverPoint.x)
        .attr('cy', hoverPoint.y)
        .attr('r', 5)
        .style('fill', '#10b981')
        .style('stroke', '#065f46')
        .style('stroke-width', 2)
        .style('opacity', 0.8)
        .style('pointer-events', 'none');
      
      // Add hover point tooltip
      g.append('text')
        .attr('class', 'hover-text')
        .attr('x', hoverPoint.x)
        .attr('y', hoverPoint.y - 15)
        .attr('text-anchor', 'middle')
        .style('font-size', '10px')
        .style('fill', '#065f46')
        .style('font-weight', 'bold')
        .style('pointer-events', 'none')
        .text(`${hoverPoint.quantity}, ${hoverPoint.margin.toFixed(1)}%`);
    }

    // Add title
    g.append('text')
      .attr('class', 'chart-title')
      .attr('x', chartWidth / 2)
      .attr('y', -5)
      .attr('text-anchor', 'middle')
      .style('font-size', '16px')
      .style('font-weight', 'bold')
      .style('fill', '#1f2937')
      .text(`${jobType.charAt(0).toUpperCase() + jobType.slice(1)} - Margin vs Quantity`);

    // Add mode indicator
    if (isAddMode) {
      g.append('text')
        .attr('class', 'mode-indicator')
        .attr('x', chartWidth - 10)
        .attr('y', 15)
        .attr('text-anchor', 'end')
        .style('font-size', '12px')
        .style('font-weight', 'bold')
        .style('fill', '#10b981')
        .text('ADD MODE - Click to add point');
    }

    // Add some visual indicators for different zones
    if (sortedPoints.length > 0) {
      // High margin zone (low quantity)
      g.append('rect')
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', chartWidth * 0.2)
        .attr('height', chartHeight)
        .style('fill', '#fef3c7')
        .style('opacity', 0.3)
        .style('pointer-events', 'none');
      
      // Add zone labels
      g.append('text')
        .attr('x', chartWidth * 0.1)
        .attr('y', 20)
        .attr('text-anchor', 'middle')
        .style('font-size', '10px')
        .style('fill', '#92400e')
        .style('font-weight', 'bold')
        .text('DIGITAL ADVANTAGE');
      
      g.append('text')
        .attr('x', chartWidth * 0.8)
        .attr('y', 20)
        .attr('text-anchor', 'middle')
        .style('font-size', '10px')
        .style('fill', '#1e40af')
        .style('font-weight', 'bold')
        .text('LITHO COMPETITIVE');
    }

  }, [controlPoints, dimensions, isLogScale, jobType, isAddMode, hoverPoint]);

  // Validation
  const validation = validateControlPoints(controlPoints);

  return (
    <div className="w-full">
      <div className="mb-4 flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">
          Pricing Curve - {jobType.charAt(0).toUpperCase() + jobType.slice(1)}
        </h3>
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-600">
            {isLogScale ? 'Logarithmic' : 'Linear'} Scale | {controlPoints.length} Control Points
          </div>
          <button
            onClick={() => {
              setIsAddMode(!isAddMode);
              setHoverPoint(null);
            }}
            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
              isAddMode
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isAddMode ? '✓ Exit Add Mode' : '+ Add Points'}
          </button>
        </div>
      </div>
      
      {/* Add Mode Instructions */}
      {isAddMode && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
          <div className="text-sm text-green-800">
            <strong>Add Mode Active:</strong> Click anywhere on the chart to add a new control point. 
            The point will be positioned at the clicked quantity with an interpolated margin from the existing curve.
            Right-click existing points to delete them.
          </div>
        </div>
      )}
      
      {/* Validation Messages */}
      {!validation.isValid && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <div className="text-sm text-red-800">
            <strong>Validation Errors:</strong>
            <ul className="mt-1 list-disc list-inside">
              {validation.errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Chart Container */}
      <div className="border border-gray-200 rounded-lg bg-white p-4">
        <svg ref={svgRef} className="w-full"></svg>
      </div>

      {/* Chart Legend */}
      <div className="mt-4 flex justify-center">
        <div className="flex items-center space-x-6 text-sm text-gray-600">
          <div className="flex items-center">
            <div className="w-4 h-0.5 bg-blue-500 mr-2"></div>
            <span>Pricing Curve</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
            <span>Control Points (Draggable)</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
            <span>New Point Preview</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-yellow-200 mr-2"></div>
            <span>Digital Advantage Zone</span>
          </div>
          {!isAddMode && (
            <div className="text-xs text-gray-500">
              Right-click points to delete
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CurveChart;